import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { assetUrl, trackEvent } from "@/lib/api";

const SLIDE_MS = 6500;

export default function Hero({ banners, onNavigate }) {
  const count = banners.length;
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (count < 2) return undefined;
    const t = setTimeout(() => setIdx((i) => (i + 1) % count), SLIDE_MS);
    return () => clearTimeout(t);
  }, [idx, count]);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  if (!count) return null;
  const banner = banners[idx];
  const words = (banner.title || "").split(" ").filter(Boolean);
  const isAnchor = (banner.button_link || "").startsWith("#");

  const handleCta = () => {
    const link = banner.button_link || "#apps";
    const label = (banner.button_text || "").trim();
    const heroEvents = {
      "explore the app": "explore_the_app",
      "join whatsapp channel": "join_whatsapp_channel",
      "download android app": "download_android_app",
    };
    trackEvent(heroEvents[label.toLowerCase()] || "hero_cta_click", { button_name: label, destination_url: link });
    if (link.startsWith("#")) onNavigate(link);
    else window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-charcoal">
      <AnimatePresence mode="sync">
        <motion.div
          key={idx}
          className="absolute inset-0"
          style={{ zIndex: 1 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
        >
          <motion.div style={{ y }} className="absolute inset-0">
            <motion.img
              src={assetUrl(banner.image)}
              alt={banner.title}
              className="w-full h-[115%] object-cover"
              initial={{ scale: 1.14 }}
              animate={{ scale: 1.0 }}
              transition={{ duration: 9, ease: "easeOut" }}
            />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/40 to-charcoal/25" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-end pt-28 md:pt-32 pb-28 md:pb-32">
        <div key={`text-${idx}`}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="text-goldlight text-xs md:text-sm uppercase tracking-[0.35em] font-medium mb-6"
            data-testid="hero-overline"
          >
            Kannur — North Kerala
          </motion.p>

          <h1
            className="font-serif font-semibold tracking-tight text-ivory leading-[1.05] text-4xl sm:text-6xl lg:text-7xl xl:text-8xl max-w-5xl"
            data-testid="hero-title"
          >
            {words.map((w, i) => (
              <span key={i} className="inline-block overflow-hidden pb-2 -mb-2 align-top">
                <motion.span
                  className="inline-block will-change-transform"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                >
                  {w}
                  {i < words.length - 1 ? " " : ""}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 + words.length * 0.07, duration: 0.8 }}
            className="mt-6 max-w-xl text-ivory/80 text-base md:text-lg leading-relaxed"
            data-testid="hero-subtitle"
          >
            {banner.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + words.length * 0.07, duration: 0.8 }}
            className="mt-10"
          >
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCta}
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-3 bg-vermillion text-ivory text-xs md:text-sm uppercase tracking-[0.2em] font-medium px-8 py-4 transition-colors duration-300 hover:bg-blood"
            >
              {banner.button_text}
              {isAnchor ? (
                <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
              ) : (
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              )}
            </motion.button>
          </motion.div>
        </div>
      </div>

      <div className="absolute z-20 bottom-10 right-6 lg:right-10 flex items-end gap-6" data-testid="hero-controls">
        <div className="hidden sm:flex items-center gap-2 pb-2">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`hero-slide-indicator-${i}`}
              aria-label={`Go to banner ${i + 1}`}
              className={`h-[3px] transition-[width,background-color] duration-500 ${
                i === idx ? "w-12 bg-gold" : "w-6 bg-ivory/30 hover:bg-ivory/60"
              }`}
            />
          ))}
        </div>
        <div className="font-serif text-ivory leading-none">
          <span className="text-4xl md:text-5xl" data-testid="hero-current-slide">0{idx + 1}</span>
          <span className="text-ivory/40 text-lg"> / 0{count}</span>
        </div>
      </div>

      <div className="absolute z-20 bottom-10 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-3" data-testid="hero-scroll-cue">
        <span className="text-ivory/50 text-[10px] uppercase tracking-[0.35em]">Scroll</span>
        <div className="w-px h-10 bg-ivory/20 overflow-hidden">
          <motion.div
            className="w-full h-1/2 bg-gold"
            animate={{ y: ["-100%", "220%"] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          />
        </div>
      </div>
    </section>
  );
}
